import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  FormInstance,
  Input,
  Row,
  Spin
} from 'antd'
import { FormItemProps } from 'antd/lib'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import {
  usePersonsServiceGetPersonListByDataEndpoint,
  usePersonsServiceGetPersonListByDataEndpointKey
} from 'src/api/queries'
import { ApiError, Permissions } from 'src/api/requests'
import {
  CUATMTreeSelect,
  GenderSelect,
  message,
  RegionSelect
} from 'src/components'
import { useUser } from 'src/hooks'
import {
  dayMonthYearFormatWithDot,
  disabledFutureDate,
  legalAgePickerValue
} from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { AddNewPerson, RegisterType } from './AddNewPerson'
import { AddPersonAlert } from './AddPersonAlert'
import { SearchPersonTable } from './SearchPersonTable'

type Props = {
  onAddClick?: (id: string) => void
  onCancel?: () => void
  selectMode?: boolean
}

const formRules: FormItemProps['rules'] = [
  {
    min: 3
  }
]

export const PersonalDataSearch = ({
  onAddClick,
  onCancel,
  selectMode
}: Props) => {
  const { t } = useLocales()
  const { hasPermission } = useUser()
  const [form] = Form.useForm()

  const [isModalOpen, setIsModalOpen] = useState(false)

  const params = Form.useWatch([], form)
  const shouldSearchInMConnect = Form.useWatch('shouldSearchInMConnect', form)
  const isAnonymous = Form.useWatch('anonymous', form)
  const residenceAddressRegion = Form.useWatch(
    ['residenceAddress', 'region'],
    form
  )

  const queryClient = useQueryClient()

  const birthDate = params?.birthDate
    ? dayjs(params.birthDate).format(dayMonthYearFormatWithDot)
    : undefined

  const birthDateFrom = birthDate
  const birthDateTo = birthDate

  const filteredParams = Object.fromEntries(
    Object.entries({
      idnp: params?.idnp,
      firstName: params?.firstName,
      lastName: params?.lastName,
      gender: params?.gender,
      testrapCode: params?.testrapCode,
      localityId: params?.residenceAddress?.locality,
      includePatients: hasPermission(Permissions.PERSONS_MENU_ACCESS_PATIENTS),
      birthDateFrom,
      birthDateTo
    }).filter(
      ([_, value]) => value !== undefined && value !== null && value !== ''
    )
  )

  const { data, refetch, isLoading, isFetched, error } =
    usePersonsServiceGetPersonListByDataEndpoint(
      {
        shouldSearchInMConnect: false,
        ...filteredParams
      },
      [],
      { enabled: false }
    )

  const allValues = Form.useWatch([], form) || {}
  const {
    idnp: _idnp,
    testrapCode: _testrapCode,
    shouldSearchInMConnect: _shouldSearchInMConnect,
    residenceAddress,
    ...otherFields
  } = allValues

  // `residenceAddress` can be null (address fields get cleared to null), and a
  // destructuring default only guards `undefined` — so read it defensively.
  const locality = residenceAddress?.locality

  const hasOtherFields =
    !!locality || Object.values(otherFields).some((value) => !!value)

  const hasIDNP = Form.useWatch('idnp', form)
  const hasTestrapCode = Form.useWatch('testrapCode', form)

  const onSubmit = async () => {
    const values = await form.validateFields()

    const hasAtLeastOneValue = Object.values(values).some((value) => !!value)

    if (!hasAtLeastOneValue) {
      message.error(t('errors.fill_at_least_one_field'))
      return
    }

    refetch()
  }

  const onCloseModal = (form?: FormInstance) => {
    queryClient.removeQueries({
      queryKey: [usePersonsServiceGetPersonListByDataEndpointKey, hasIDNP],
      exact: true
    })

    form?.resetFields()
    setIsModalOpen((prev) => !prev)
  }

  const handleCancel = () => {
    queryClient.removeQueries({
      queryKey: [usePersonsServiceGetPersonListByDataEndpointKey],
      exact: false
    })
    form.resetFields()
    onCancel && onCancel()
  }

  useEffect(() => {
    const typedError = error as {
      body: {
        errors: ApiError
      }
    }

    if (typedError && !shouldSearchInMConnect)
      formErrorHandler(typedError?.body?.errors)
  }, [error, shouldSearchInMConnect])

  const renderContent = () => {
    if (isLoading) return <Spin />
    if (isFetched && !data?.data?.length) return <AddPersonAlert form={form} />

    if (isFetched && data?.data) {
      return (
        <SearchPersonTable
          selectMode={selectMode}
          isLoading={isLoading}
          onAddClick={onAddClick}
          onCancel={handleCancel}
          data={data?.data}
        />
      )
    }
  }

  return (
    <>
      <Form requiredMark={false} layout="vertical" form={form}>
        <Flex vertical gap={24}>
          <Flex vertical gap={24}>
            <Row gutter={[24, 24]}>
              <Col xs={24} lg={12}>
                <Form.Item
                  name="lastName"
                  label={t('form.last_name')}
                  rules={[...(formRules ?? [])]}
                >
                  <Input
                    placeholder={t('form.last_name')}
                    disabled={
                      hasIDNP || hasTestrapCode || shouldSearchInMConnect
                    }
                  />
                </Form.Item>
              </Col>

              <Col xs={24} lg={12}>
                <Form.Item
                  name="firstName"
                  label={t('form.first_name')}
                  rules={[...(formRules ?? [])]}
                >
                  <Input
                    placeholder={t('form.first_name')}
                    disabled={
                      hasIDNP || hasTestrapCode || shouldSearchInMConnect
                    }
                  />
                </Form.Item>
              </Col>

              {!isAnonymous && (
                <Col xs={24} lg={12}>
                  <Form.Item
                    name="idnp"
                    label={t('form.idnp')}
                    rules={[
                      {
                        min: 13,
                        max: 13,
                        required: !!shouldSearchInMConnect
                      }
                    ]}
                  >
                    <Input
                      placeholder={t('form.idnp')}
                      className="full-width"
                      disabled={hasOtherFields || hasTestrapCode}
                    />
                  </Form.Item>
                </Col>
              )}

              {!isAnonymous && (
                <Col xs={24} lg={12}>
                  <Form.Item name="testrapCode" label={t('form.testrapCode')}>
                    <Input
                      placeholder={t('form.testrapCode')}
                      className="full-width"
                      disabled={
                        hasOtherFields || hasIDNP || shouldSearchInMConnect
                      }
                    />
                  </Form.Item>
                </Col>
              )}

              <Col xs={24} lg={12}>
                <Form.Item name="birthDate" label={t('form.birth_date')}>
                  <DatePicker
                    className="full-width"
                    format={dayMonthYearFormatWithDot}
                    placement="bottomRight"
                    disabledDate={disabledFutureDate}
                    defaultPickerValue={legalAgePickerValue}
                    placeholder={t('form.birth_date')}
                    disabled={
                      hasIDNP || hasTestrapCode || shouldSearchInMConnect
                    }
                  />
                </Form.Item>
              </Col>

              <Col span={24} lg={12}>
                <Form.Item name="gender" label={t('form.gender')}>
                  <GenderSelect
                    allowClear
                    disabled={
                      hasIDNP || hasTestrapCode || shouldSearchInMConnect
                    }
                  />
                </Form.Item>
              </Col>

              <Col xs={24} lg={12}>
                <Form.Item
                  name={['residenceAddress', 'region']}
                  label={t('organizational_structure.region')}
                >
                  <RegionSelect
                    onChange={() => {
                      form.setFieldValue('locality', null)
                    }}
                    placeholder={t('organizational_structure.region')}
                    disabled={
                      hasIDNP || hasTestrapCode || shouldSearchInMConnect
                    }
                  />
                </Form.Item>
              </Col>

              <Col xs={24} lg={12}>
                <Form.Item
                  name={['residenceAddress', 'locality']}
                  label={t('organizational_structure.city')}
                >
                  <CUATMTreeSelect
                    placeholder={t('organizational_structure.city')}
                    elementId={residenceAddressRegion}
                    disabled={!residenceAddressRegion}
                  />
                </Form.Item>
              </Col>
            </Row>

            {!shouldSearchInMConnect && (
              <Flex justify="center" gap={8}>
                <Button
                  type="primary"
                  onClick={onSubmit}
                  disabled={!hasOtherFields && !hasIDNP && !hasTestrapCode}
                >
                  {t('persons.search_persons')}
                </Button>

                {hasPermission(
                  Permissions.PERSONS_PERSONS_CREATE_INSTITUTION
                ) &&
                  !!data?.data?.length && (
                    <Button type="primary" onClick={() => setIsModalOpen(true)}>
                      {t('persons.add_new_person')}
                    </Button>
                  )}
              </Flex>
            )}
          </Flex>

          {renderContent()}
        </Flex>
      </Form>

      {isModalOpen && (
        <AddNewPerson
          initialValues={form.getFieldsValue()}
          initialRegisterType={
            hasIDNP ? RegisterType.PERSON : RegisterType.ANONYMOUS
          }
          onCloseModal={onCloseModal}
        />
      )}
    </>
  )
}
